import { useEffect } from 'react';
import { useTheme } from 'next-themes';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

type Theme = 'light' | 'dark' | 'system';

export const usePersistedTheme = () => {
  const { user } = useAuth();
  const { theme, setTheme: setNextTheme } = useTheme();
  const queryClient = useQueryClient();

  // Fetch theme from database
  const { data: dbTheme, isLoading } = useQuery({
    queryKey: ['user-theme', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      
      const { data, error } = await supabase
        .from('profiles')
        .select('theme')
        .eq('id', user.id)
        .single();
      
      if (error) throw error;
      return data?.theme as Theme | null;
    },
    enabled: !!user?.id,
  });

  // Update theme in database
  const updateThemeMutation = useMutation({
    mutationFn: async (newTheme: Theme) => {
      if (!user?.id) throw new Error('User not authenticated');
      
      const { error } = await supabase
        .from('profiles')
        .update({ theme: newTheme })
        .eq('id', user.id);
      
      if (error) throw error;
      return newTheme;
    },
    onMutate: async (newTheme) => {
      // Optimistically update UI
      setNextTheme(newTheme);
      
      // Update localStorage for fallback
      localStorage.setItem('theme', newTheme);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-theme', user?.id] });
    },
    onError: (error) => {
      console.error('Failed to update theme:', error);
    },
  });

  // Sync database theme with next-themes on mount and DB updates only
  useEffect(() => {
    if (!dbTheme) return;
    // Only sync from DB when not mid-update
    if (dbTheme !== theme && !updateThemeMutation.isPending) {
      setNextTheme(dbTheme);
    }
  }, [dbTheme, setNextTheme]);

  // For unauthenticated users, use next-themes directly with localStorage fallback
  const setTheme = (newTheme: Theme) => {
    if (user?.id) {
      updateThemeMutation.mutate(newTheme);
    } else {
      setNextTheme(newTheme);
      localStorage.setItem('theme', newTheme);
    }
  };

  return {
    theme: (theme || 'light') as Theme,
    setTheme,
    isLoading: user?.id ? isLoading : false,
  };
};
